import { useEffect, useRef, useState } from 'react';
import {
  useEditor,
  EditorContent,
  useEditorState,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  Extension,
  type NodeViewProps,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
// [评论·2026-08-28] 字数硬上限(等价 antd maxLength):到上限就敲不进去。
// Extension 从 @tiptap/react 取(@tiptap/core 非直接依赖,但 react 已重导出)。
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import TextAlign from '@tiptap/extension-text-align';
import Image, { type ImageOptions } from '@tiptap/extension-image';
import { marked } from 'marked';
import { useAuthStore } from '@stores/authStore';
import { uploadEditorImage } from '../../lib/services/agents/mediaUploadApi';
import {
  PhotoIcon,
  Bars3BottomLeftIcon,
  Bars3Icon,
  Bars3BottomRightIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

/**
 * WYSIWYG draft editor (Figma 21486:9301) built on TipTap. HTML is the single
 * canonical format — markdown drafts are converted to HTML on load (marked) and
 * everything is edited + exported as HTML (`getHTML()`), which is what Shopify
 * `body_html` wants. The toolbar (bold/italic/underline, H1/H2/paragraph,
 * image, left/center/right align) maps to real editor commands, so nothing here
 * is decorative. Hover an image to Regenerate it.
 */

/** Block-level HTML structure — the real signal that a body was authored/saved as
 *  HTML. `looksLikeHtml` also fires on inline `<img>`/`<a>`, but blog bodies arrive as
 *  MARKDOWN (## headings, blank-line paragraphs) with bare `<img>` tags spliced in.
 *  Those must NOT be mistaken for HTML: doing so skips `marked` and the headings /
 *  paragraph breaks leak into the editor as literal `##` text with everything run
 *  together (only the images render). marked passes the raw `<img>` through untouched,
 *  so running it on a hybrid body converts the markdown AND keeps the pictures. */
function hasBlockHtml(s: string): boolean {
  return /<(?:p|div|h[1-6]|ul|ol|li|hr|blockquote|table|thead|tbody|tr|td|th|section|article|figure|pre)(?=[\s/>])/i.test(
    s
  );
}

/** Normalise incoming draft content (markdown OR HTML) to HTML for the editor.
 *  Defensive: a malformed value must never throw here (YEP-708) — fall back to
 *  the raw string so the editor (and its boundary) can decide how to degrade. */
function toHtml(value: string): string {
  if (!value) return '';
  // Genuine HTML (has block structure) is handed to the editor as-is. A markdown body
  // — even one carrying inline <img>/<a> — goes through marked so its structure renders.
  if (hasBlockHtml(value)) return value;
  try {
    return marked.parse(value, { async: false }) as string;
  } catch {
    return value;
  }
}

/**
 * [评论·2026-08-28] 计数口径:纯文本 + 段落间用单个 '\n' 分隔。计数器显示和硬上限都走这一个
 * 函数,保证「正好 N/N 时敲不进去」,不会出现两边差一个数的错位。
 */
const docLen = (doc: ProseMirrorNode) => doc.textBetween(0, doc.content.size, '\n').length;

/**
 * 字数硬上限扩展 —— 像 antd 输入框的 maxLength:达到上限后,任何"会让总长继续变长"的输入
 * (打字/粘贴/拖入)都被拒绝,输入不进去;删除、选中替换这类"不增长"的编辑照常放行,所以一篇
 * 本来就超限的历史草稿也能删到限内。charLimit 不传(Blog)时不挂插件,零开销。
 */
const CharacterLimit = Extension.create<{ limit: number | null }>({
  name: 'characterLimit',
  addOptions() {
    return { limit: null };
  },
  addProseMirrorPlugins() {
    const limit = this.options.limit;
    if (limit == null) return [];
    return [
      new Plugin({
        key: new PluginKey('characterLimit'),
        filterTransaction: (tr, state) => {
          if (!tr.docChanged) return true;
          // 当前为空 → 放行:这既是编辑器挂载后"一次性灌入初始草稿"的事务(哪怕草稿本身
          // 已超 2200,也得让它加载出来,红色计数器 + 之后不能再变长即可),也覆盖首次输入。
          if (docLen(state.doc) === 0) return true;
          const next = docLen(tr.doc);
          if (next <= limit) return true;
          // 已经超限时只放行"不再变长"的编辑(删/替换),挡住继续加字。
          return next <= docLen(state.doc);
        },
      }),
    ];
  },
});

/** Image node with a hover "Regenerate" button (Figma: circular icon, top-right). */
function ImageNodeView({ node, extension }: NodeViewProps) {
  const src = node.attrs.src as string;
  const alt = (node.attrs.alt as string) ?? '';
  const onRegenerate = (extension.options as RegenImageOptions).onRegenerate;
  return (
    <NodeViewWrapper className="group relative my-3 inline-block max-w-full" data-drag-handle>
      <img src={src} alt={alt} className="max-h-80 max-w-full rounded-xl object-cover" />
      {/* [评论·⑥] 去掉图片盲 Regenerate 按钮——无 brief 重掷等于老虎机；改图统一走 Maya Chat 文字（"换白底""换张 hero"）。 */}
    </NodeViewWrapper>
  );
}

interface RegenImageOptions extends ImageOptions {
  onRegenerate?: (src: string) => void;
}

const RegenImage = Image.extend<RegenImageOptions>({
  addOptions() {
    return { ...(this.parent?.() as ImageOptions), onRegenerate: undefined };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});

function ToolbarButton({
  active,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={`flex h-8 min-w-8 items-center justify-center rounded-md px-1.5 text-sm font-semibold transition ${
        active ? 'bg-[#6b33ff] text-white' : 'text-[#475467] hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

const Divider = () => <span className="mx-1 h-5 w-px bg-gray-200" />;

/** [3.5 #14] 从 drop/paste 事件里挑出图片文件(拖文字、拖链接、贴文本都不算)。 */
export function imageFilesOf(source: DataTransfer | null): File[] {
  if (!source) return [];
  return Array.from(source.files ?? []).filter(f => f.type.startsWith('image/'));
}

/**
 * [3.5 #14] 拖进来/粘贴进来的图片该落在哪。
 *
 * 正文插图只有 Shopify Blog(imagePlacement==='inline')有;IG / FB 是「一张图 +
 * 一段文案」的形态,只能换主图 —— 所以它们的拖放一律收到主图上,不进正文。
 * 抽成纯函数是为了能单独测:拖放本身跑在 ProseMirror 里,jsdom 下测不动。
 */
export function imageDropTarget(imagePlacement: 'inline' | 'top'): 'body' | 'hero' {
  return imagePlacement === 'top' ? 'hero' : 'body';
}

export default function RichDraftEditor({
  value,
  onChange,
  onRegenerateImage,
  imagePlacement = 'inline',
  imageUrl,
  onImageChange,
  // [需求⑦·平台原生编辑器] 轻量模式（IG/FB/X）：隐藏富文本格式化工具栏，仅保留纯文本+图片+hashtag。
  lightweight = false,
  // [需求⑦·平台原生编辑器] 各平台字数上限，传入即在底部实时显示 当前字数/上限（超限标红）。
  charLimit,
}: {
  value: string;
  onChange: (html: string) => void;
  onRegenerateImage?: (src: string) => void;
  /** [需求⑦·平台原生编辑器] true=社媒轻量编辑器（无格式化/对齐工具）；false=Blog 富文本（默认）。 */
  lightweight?: boolean;
  /** [需求⑦·平台原生编辑器] 平台字数上限（如 X=280 / IG=2200 / FB=63206）；不传=不限（Blog）。 */
  charLimit?: number;
  /** Where an added image lands: 'inline' at the cursor (Shopify blog body can
   *  have images anywhere) or 'top' as the single leading post image
   *  (Instagram/Facebook attach one image above the caption). */
  imagePlacement?: 'inline' | 'top';
  /** 'top' mode only: the post's single hero image (a separate field from the
   *  caption body), rendered as a preview above the editor. */
  imageUrl?: string;
  /** 'top' mode only: called when the hero image is attached/replaced. */
  onImageChange?: (url: string) => void;
}) {
  /**
   * [3.5 #14] 拖拽/粘贴图片的真正处理函数。editorProps 在 useEditor 里就要引用它,
   * 而它需要 userId、上传函数和 editor 本身 —— 那些都在下面才拿到,所以用 ref 转发,
   * 赋值放在这些依赖都就绪之后(编辑器要收到 drop 至少得先挂载并绘制过一帧)。
   */
  const imageDropRef = useRef<((files: File[], at: number | null) => boolean) | null>(null);

  const editor = useEditor({
    immediatelyRender: false,
    // Let TipTap validate the initial content instead of throwing during the
    // render that opens the editor — a history draft's raw body_html can be
    // structure ProseMirror rejects; degrade gracefully (YEP-708).
    enableContentCheck: true,
    // [3.5 #14 / 2026-09-14] 社媒(lightweight = IG/FB)正文 = 纯文本:
    //   关掉输入/粘贴规则 → markdown 式输入(`## `、`*bold*`)与「粘贴富文本自动套格式」都不生效;
    //   下面 extensions 再禁掉所有字体/块级格式扩展,并去掉正文 Image 节点。Blog 仍是全量富文本。
    enableInputRules: !lightweight,
    enablePasteRules: !lightweight,
    onContentError: ({ error }) => {
      console.warn('[RichDraftEditor] invalid initial content, loaded best-effort:', error);
    },
    extensions: lightweight
      ? [
          // 纯文本:只保留 StarterKit 默认的段落 / 换行 / 撤销等基础节点,禁掉一切字体与块级格式。
          // Underline 由 StarterKit v3 内置,一并禁;不加 TextAlign(对齐属格式);
          // 不加 RegenImage —— IG/FB 主图走独立的 image_url 字段(上方单独 <img> 预览),正文不放图(验收 #17)。
          StarterKit.configure({
            bold: false,
            italic: false,
            strike: false,
            code: false,
            underline: false,
            heading: false,
            bulletList: false,
            orderedList: false,
            listItem: false,
            blockquote: false,
            codeBlock: false,
            horizontalRule: false,
          }),
          // 社媒才传 charLimit(IG 2200 等),挂硬上限。
          CharacterLimit.configure({ limit: charLimit ?? null }),
        ]
      : [
          StarterKit,
          TextAlign.configure({ types: ['heading', 'paragraph'] }),
          RegenImage.configure({ inline: false, onRegenerate: onRegenerateImage }),
          // [评论·2026-08-28] 传了 charLimit(社媒)才挂硬上限;Blog 不传=不限。
          CharacterLimit.configure({ limit: charLimit ?? null }),
        ],
    // Mount EMPTY, then seed the content in an effect (below). Setting HTML that
    // contains an <img> during the synchronous mount makes the React image
    // node-view call `flushSync` inside React's render phase, which throws under
    // React 19 and drops the whole editor to the plain-textarea fallback (YEP-708,
    // "editor shows raw <p>/<h2>"). Seeding post-commit keeps flushSync legal.
    content: '',
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none min-h-[220px] px-4 py-3 text-[#322d40] leading-relaxed focus:outline-none [&_h1]:text-2xl [&_h2]:text-xl [&_img]:rounded-xl',
      },
      // [3.5 #14] 拖进来/粘贴进来的图片一律由我们接手,不让浏览器与 ProseMirror
      // 走默认行为。两个原因:
      //   ① 默认行为会把本地文件塞成 blob:/data: 的 <img> —— 商家在编辑器里看得见,
      //      发布时 Shopify 取不到,等于埋一颗雷;
      //   ② 正文插图只有 Shopify Blog 有(imagePlacement==='inline')。IG/FB 是
      //      「一张图 + 一段文案」的形态,只能换主图,不能往正文里塞第二张。
      // 真正的处理逻辑在下面的 imageDropRef —— 它要用到 userId / 上传函数 / editor,
      // 这些都在 useEditor 之后才声明,所以这里只做转发。
      handleDrop: (view, event) => {
        const files = imageFilesOf((event as DragEvent).dataTransfer);
        if (files.length === 0) return false;
        event.preventDefault();
        const at =
          view.posAtCoords({
            left: (event as DragEvent).clientX,
            top: (event as DragEvent).clientY,
          })?.pos ?? null;
        return imageDropRef.current?.(files, at) ?? true;
      },
      handlePaste: (_view, event) => {
        const files = imageFilesOf((event as ClipboardEvent).clipboardData);
        if (files.length === 0) return false;
        event.preventDefault();
        return imageDropRef.current?.(files, null) ?? true;
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Seed the initial content once, AFTER mount (see note above). `false` keeps it
  // from firing onUpdate (this is loading existing content, not a user edit).
  const seededRef = useRef(false);
  useEffect(() => {
    if (!editor || seededRef.current) return;
    seededRef.current = true;
    const html = toHtml(value);
    if (html) editor.commands.setContent(html, { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const userId = useAuthStore(s => s.user?.id);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  // Where the NEXT upload lands. 'hero' → the separate cover/hero image
  // (`image_url`, shown in the preview — a blog cover or an IG/FB post image);
  // 'body' → inserted into the article body at the cursor (blog only). Set right
  // before opening the picker so the shared file input routes correctly.
  // MUST be declared with the other hooks (before the `if (!editor) return null`
  // below) — a hook after a conditional return breaks the rules of hooks.
  const uploadTargetRef = useRef<'hero' | 'body'>('body');

  // Re-render the toolbar when the active formatting under the cursor changes.
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e
        ? {
            bold: e.isActive('bold'),
            italic: e.isActive('italic'),
            underline: e.isActive('underline'),
            h1: e.isActive('heading', { level: 1 }),
            h2: e.isActive('heading', { level: 2 }),
            paragraph: e.isActive('paragraph'),
            left: e.isActive({ textAlign: 'left' }),
            center: e.isActive({ textAlign: 'center' }),
            right: e.isActive({ textAlign: 'right' }),
            // [需求⑦·平台原生编辑器] 纯文本字符数，用于社媒字数上限实时提示。
            // [评论·2026-08-28] 与 CharacterLimit 硬上限用同一 docLen,保证显示数与"敲不动"的点一致。
            chars: docLen(e.state.doc),
          }
        : null,
  });

  if (!editor) return null;

  // Place an image per the target's rules: blog → at the cursor (inline, any
  // number); IG/FB → a single leading image at the very top (replace an existing
  // one), since those are one-image-above-the-caption posts.
  const placeImage = (url: string) => {
    if (imagePlacement === 'top') {
      const chain = editor.chain().focus();
      const first = editor.state.doc.firstChild;
      if (first && first.type.name === 'image') {
        // Replace the current leading image so there's only ever one.
        chain.setNodeSelection(0).deleteSelection();
      }
      chain.insertContentAt(0, { type: 'image', attrs: { src: url } }).run();
    } else {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const applyImage = (url: string) => {
    if (uploadTargetRef.current === 'hero' && onImageChange) onImageChange(url);
    else placeImage(url);
  };

  // Local file upload: pick → upload to the tenant-isolated public media bucket →
  // apply the returned public URL (Shopify can fetch it on publish). Falls back
  // to a URL prompt only if the user isn't signed in (no tenant to upload under).
  const onPickImage = (target: 'hero' | 'body') => {
    uploadTargetRef.current = target;
    if (!userId) {
      const url = window.prompt('Image URL');
      if (url) applyImage(url);
      return;
    }
    fileInputRef.current?.click();
  };
  // Toolbar image button: a blog inserts into the body; an IG/FB post has no body
  // images, so its button sets the single hero image.
  const onToolbarImage = () => onPickImage(imagePlacement === 'top' ? 'hero' : 'body');

  /**
   * 上传到租户隔离的公共媒体桶,再把返回的公共 URL 应用上去。
   * `at` 只在正文插图时有意义(拖到哪一段就插在哪),null=按 applyImage 的既有规则走。
   */
  const uploadAndApply = async (file: File, at: number | null) => {
    if (!userId) return;
    setUploading(true);
    try {
      const { url } = await uploadEditorImage(userId, file);
      if (at != null && uploadTargetRef.current === 'body') {
        editor.chain().focus().insertContentAt(at, { type: 'image', attrs: { src: url } }).run();
      } else {
        applyImage(url);
      }
    } catch (err) {
      console.error('[RichDraftEditor] image upload failed', err);
      window.alert('Image upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be re-picked later
    if (!file) return;
    await uploadAndApply(file, null);
  };

  // [3.5 #14] 接住 editorProps 转发过来的拖拽/粘贴。
  //   Blog(inline):上传后插在拖放位置 —— 这一步就走 onUpdate → onChange,
  //     所以预览区拿到的是带图的正文(验收 #19:上传后预览要看得见那张图)。
  //   IG / FB(top):不进正文,一律当主图处理(applyImage → 主图字段或唯一的首图)。
  imageDropRef.current = (files, at) => {
    const file = files[0];
    if (!file) return true;
    if (!userId) {
      window.alert('Please sign in again before adding an image.');
      return true;
    }
    const target = imageDropTarget(imagePlacement);
    uploadTargetRef.current = target;
    void uploadAndApply(file, target === 'body' ? at : null);
    return true; // 已接手,ProseMirror 不要再走默认行为
  };

  return (
    <div className="mt-2 overflow-hidden rounded-2xl border border-gray-200">
      {/* Toolbar (Figma 21486:9301) */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-white px-2 py-1.5">
        {/* [需求⑦·平台原生编辑器] Blog=富文本全工具栏；社媒(IG/FB/X)轻量模式隐藏格式化+对齐按钮，仅保留图片。
            原按钮不删除，用 lightweight 条件包裹。 */}
        {!lightweight && (
          <>
            <ToolbarButton
              active={state?.bold}
              onClick={() => editor.chain().focus().toggleBold().run()}
              title="Bold"
            >
              <b>B</b>
            </ToolbarButton>
            <ToolbarButton
              active={state?.italic}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              title="Italic"
            >
              <i>I</i>
            </ToolbarButton>
            <ToolbarButton
              active={state?.underline}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              title="Underline"
            >
              <u>U</u>
            </ToolbarButton>
            <Divider />
            <ToolbarButton
              active={state?.h1}
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              title="Heading 1"
            >
              H1
            </ToolbarButton>
            <ToolbarButton
              active={state?.h2}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              title="Heading 2"
            >
              H2
            </ToolbarButton>
            <ToolbarButton
              active={state?.paragraph}
              onClick={() => editor.chain().focus().setParagraph().run()}
              title="Paragraph"
            >
              ¶
            </ToolbarButton>
            <Divider />
          </>
        )}
        <ToolbarButton
          onClick={() => !uploading && onToolbarImage()}
          title={
            uploading
              ? 'Uploading image…'
              : imagePlacement === 'top'
                ? 'Set the post image (shown at the top)'
                : 'Insert image at cursor'
          }
        >
          {uploading ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
          ) : (
            <PhotoIcon className="h-4 w-4" />
          )}
        </ToolbarButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={onFileChange}
        />
        {/* [需求⑦·平台原生编辑器] 对齐按钮同属富文本能力，轻量模式隐藏 */}
        {!lightweight && (
          <>
            <Divider />
            <ToolbarButton
              active={state?.left}
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              title="Align left"
            >
              <Bars3BottomLeftIcon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              active={state?.center}
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              title="Align center"
            >
              <Bars3Icon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              active={state?.right}
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              title="Align right"
            >
              <Bars3BottomRightIcon className="h-4 w-4" />
            </ToolbarButton>
          </>
        )}
      </div>

      {/* Hero / cover image — a separate field (`image_url`): the blog's cover or
          the IG/FB post's single image, shown above the body. Hover to
          replace/regenerate. For a blog, inline body images still go in via the
          toolbar button at the cursor; this preview is the cover only. */}
      {imageUrl ? (
        <div className="group relative border-b border-gray-200 bg-gray-50 p-3">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">
            {imagePlacement === 'inline' ? 'Cover image' : 'Post image'}
          </div>
          <img
            src={imageUrl}
            alt=""
            className="mx-auto max-h-72 max-w-full rounded-xl object-contain"
          />
          <div className="absolute right-4 top-8 hidden gap-1.5 group-hover:flex">
            <button
              type="button"
              onClick={() => !uploading && onPickImage('hero')}
              title="Replace image"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-[#2c3437] shadow ring-1 ring-black/5 hover:bg-white"
            >
              {uploading ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <PhotoIcon className="h-4 w-4" />
              )}
            </button>
            {/* [评论·⑥] 去掉封面图盲 Regenerate 按钮（保留 Replace/上传）；改图走 Maya Chat 文字。 */}
          </div>
        </div>
      ) : (
        onImageChange && (
          <button
            type="button"
            onClick={() => !uploading && onPickImage('hero')}
            className="flex w-full items-center justify-center gap-2 border-b border-dashed border-gray-300 bg-gray-50 py-6 text-sm font-medium text-[#747c80] transition hover:bg-gray-100"
          >
            {uploading ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <PhotoIcon className="h-4 w-4" />
            )}
            {uploading
              ? 'Uploading…'
              : imagePlacement === 'inline'
                ? 'Add a cover image'
                : 'Attach an image for this post'}
          </button>
        )
      )}

      <EditorContent editor={editor} />

      {/* [需求⑦·平台原生编辑器] 各平台字数上限实时提示（仅社媒传入 charLimit；Blog 不传=不显示）。
          ⚠ 交互细节待确认：编辑器那屏因 Figma 额度限流未截到，字数上限数值（IG 2200 / FB 63206 / X 280）
          与提示的样式/位置先按平台常规实现，待设计确认后调整。 */}
      {charLimit != null && (
        <div
          className={`flex justify-end border-t border-gray-200 bg-white px-3 py-1.5 text-xs ${
            (state?.chars ?? 0) > charLimit ? 'font-semibold text-red-500' : 'text-gray-400'
          }`}
        >
          {(state?.chars ?? 0).toLocaleString()} / {charLimit.toLocaleString()}
        </div>
      )}
    </div>
  );
}
